import { useState, useEffect } from "react";
import api from "../../api/client";
import { useToast } from "../../hooks/use-toast";
import KPITooltip, { KPI_TOOLTIPS } from "../../components/KPITooltip";
import AdminStudentDossier from "../../components/AdminStudentDossier";
import ReminderSettingsCard from "../../components/ReminderSettingsCard";
import NotificationPreferences from "../../components/NotificationPreferences";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from "recharts";

const KPICard = ({ label, value, icon, color = "", sub, tooltip }) => (
  <div className="glass-card glass-card-hover rounded-2xl p-5 animate-fade-in-up">
    <div className="flex items-center justify-between mb-3">
      <div className={`icon-circle ${color}`}>
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
        </svg>
      </div>
    </div>
    <div className="metric-number">{value}</div>
    <div className="metric-label mt-1">
      {tooltip ? (
        <KPITooltip content={tooltip} position="bottom">{label}</KPITooltip>
      ) : label}
    </div>
    {sub && <div className="text-xs text-gold-dark mt-2 font-medium">{sub}</div>}
  </div>
);

const StatusBadge = ({ role }) => {
  const map = { admin: "badge-admin", coach: "badge-coach", student: "badge-student" };
  const labels = { admin: "Admin", coach: "Coach", student: "Élève" };
  return <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${map[role] || "bg-slate-100 text-slate-600"}`}>{labels[role] || role}</span>;
};

const COLORS = ['#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4'];

export default function AdminDashboard({ activeTab, setActiveTab }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [riskScores, setRiskScores] = useState([]);
  const [coachStats, setCoachStats] = useState([]);
  const [integrationData, setIntegrationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [userForm, setUserForm] = useState({ email: "", password: "", first_name: "", last_name: "", role: "student" });
  const [assignForm, setAssignForm] = useState({ student_id: "", coach_id: "" });
  const { toast } = useToast();
  
  // Filters
  const [filterCoach, setFilterCoach] = useState("all");
  const [filterDate, setFilterDate] = useState("all");

  const dateFilters = [
    { value: "all", label: "Toutes les dates" },
    { value: "1", label: "< 1 mois" },
    { value: "2", label: "1-2 mois" },
    { value: "3", label: "2-3 mois" },
    { value: "6", label: "3-6 mois" },
    { value: "12", label: "6-12 mois" },
    { value: "18", label: "1-1.5 ans" },
    { value: "24", label: "1.5-2 ans" },
    { value: "24+", label: "2 ans et +" },
  ];

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, assignRes, riskRes, coachStatsRes, integrationRes] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/assignments"),
        api.get("/parcours/all-risk-scores"),
        api.get("/coaches/stats"),
        api.get("/students/by-integration-date"),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setAssignments(assignRes.data);
      setRiskScores(riskRes.data || []);
      setCoachStats(coachStatsRes.data || []);
      setIntegrationData(integrationRes.data || []);
    } catch (e) {
      console.error("Admin fetchData error:", e);
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de charger certaines données" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Get coaches and students lists
  const coaches = users.filter(u => u.role === "coach");
  const students = users.filter(u => u.role === "student");
  const assignedStudentIds = assignments.map(a => a.student_id);
  const unassignedStudents = students.filter(s => !assignedStudentIds.includes(s.id));
  
  // Filter risk scores by coach and date
  const getFilteredRiskScores = () => {
    return riskScores.filter(student => {
      // Filter by coach
      if (filterCoach !== "all") {
        const assignment = assignments.find(a => a.student_id === student.student_id);
        if (!assignment || assignment.coach_id !== filterCoach) return false;
      }
      
      // Filter by date - get student info from users
      if (filterDate !== "all") {
        const studentUser = users.find(u => u.id === student.student_id);
        if (!studentUser || !studentUser.created_at) return false;
        
        const createdAt = new Date(studentUser.created_at);
        const now = new Date();
        const monthsAgo = (now - createdAt) / (1000 * 60 * 60 * 24 * 30);
        
        if (filterDate === "1" && monthsAgo >= 1) return false;
        if (filterDate === "2" && (monthsAgo < 1 || monthsAgo >= 2)) return false;
        if (filterDate === "3" && (monthsAgo < 2 || monthsAgo >= 3)) return false;
        if (filterDate === "6" && (monthsAgo < 3 || monthsAgo >= 6)) return false;
        if (filterDate === "12" && (monthsAgo < 6 || monthsAgo >= 12)) return false;
        if (filterDate === "18" && (monthsAgo < 12 || monthsAgo >= 18)) return false;
        if (filterDate === "24" && (monthsAgo < 18 || monthsAgo >= 24)) return false;
        if (filterDate === "24+" && monthsAgo < 24) return false;
      }
      
      return true;
    });
  };

  const filteredRiskScores = getFilteredRiskScores();

  // Stats by filter
  const filteredStats = {
    high: filteredRiskScores.filter(r => r.level === "high").length,
    medium: filteredRiskScores.filter(r => r.level === "medium").length,
    low: filteredRiskScores.filter(r => r.level === "low" || r.level === "success").length,
    total: filteredRiskScores.length
  };

  // Prepare chart data for risk distribution
  const riskDistributionData = [
    { name: "Risque élevé", value: filteredStats.high, color: "#ef4444" },
    { name: "Risque modéré", value: filteredStats.medium, color: "#f59e0b" },
    { name: "En bonne voie", value: filteredStats.low, color: "#10b981" },
  ].filter(d => d.value > 0);

  // Prepare chart data for coach performance
  const coachPerformanceData = coachStats.map(c => ({
    name: c.name.split(" ")[0], // First name only for chart
    fullName: c.name,
    eleves: c.students_count,
    risque: c.avg_risk_score,
    reussite: c.success_rate,
  }));

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        const payload = { first_name: userForm.first_name, last_name: userForm.last_name, role: userForm.role, is_active: userForm.is_active };
        if (userForm.password) payload.password = userForm.password;
        await api.put(`/admin/users/${editingUser.id}`, payload);
        toast({ title: "Utilisateur mis à jour" });
      } else {
        await api.post("/admin/users", userForm);
        toast({ title: "Utilisateur créé" });
      }
      setShowUserModal(false);
      setEditingUser(null);
      setUserForm({ email: "", password: "", first_name: "", last_name: "", role: "student" });
      fetchData();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e.response?.data?.detail || "Erreur" });
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast({ title: "Utilisateur supprimé" });
      fetchData();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e.response?.data?.detail || "Erreur" });
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
      toast({ title: u.is_active ? "Compte désactivé" : "Compte activé" });
      fetchData();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const handleAssign = async () => {
    try {
      await api.post("/admin/assignments", assignForm);
      toast({ title: "Affectation créée" });
      setShowAssignModal(false);
      setAssignForm({ student_id: "", coach_id: "" });
      fetchData();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e.response?.data?.detail || "Erreur" });
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm("Supprimer cette affectation ?")) return;
    try {
      await api.delete(`/admin/assignments/${id}`);
      toast({ title: "Affectation supprimée" });
      fetchData();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const tabs = [
    { key: "dashboard", label: "Tableau de bord" },
    { key: "statistics", label: "Statistiques avancées" },
    { key: "coach-stats", label: "Stats par Coach" },
    { key: "users", label: "Utilisateurs" },
    { key: "assignments", label: "Affectations" },
    { key: "relances", label: "Relances" },
  ];

  const handleRefresh = async () => {
    setLoading(true);
    await fetchData();
    toast({ title: "Données actualisées" });
  };

  if (loading) return (
    <div className="p-6 space-y-4">
      {[1,2,3,4].map(i => <div key={i} className="skeleton h-24 rounded-2xl" />)}
    </div>
  );

  return (
    <div className="p-6 space-y-8">
      {/* Header with refresh button */}
      <div className="flex justify-between items-center pb-2">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "var(--serif)" }}>
          Administration
        </h1>
        <button
          onClick={handleRefresh}
          className="btn-secondary text-sm flex items-center gap-2"
          data-testid="refresh-data-btn"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Actualiser
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 bg-paper-2 p-1 rounded-full w-fit border border-line mb-2">
        {tabs.map(t => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setActiveTab(t.key)}
            className={`tab-button ${activeTab === t.key ? "tab-button-active" : ""}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === "dashboard" && (
        <div className="space-y-8 animate-fade-in">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            <KPICard label="Élèves actifs" value={stats?.total_students || 0} icon="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" color="icon-circle--ink" tooltip={KPI_TOOLTIPS.totalStudents} />
            <KPICard label="Coachs actifs" value={stats?.total_coaches || 0} icon="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" color="icon-circle--teal" tooltip={KPI_TOOLTIPS.totalCoaches} />
            <KPICard label="Affectations" value={stats?.total_assignments || 0} icon="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" tooltip={KPI_TOOLTIPS.totalAssignments} />
            <KPICard label="Progression moy." value={`${stats?.average_progress || 0}%`} icon="M13 10V3L4 14h7v7l9-11h-7z" color="icon-circle--teal" sub={`${stats?.completed_steps || 0}/${stats?.total_steps || 0} étapes`} tooltip={KPI_TOOLTIPS.averageProgress} />
          </div>

          {stats?.coach_performance?.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
              <div className="p-5 border-b border-slate-50">
                <h3 className="font-semibold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>Performance des coachs</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Coach</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Élèves</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Étapes créées</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Progression moy.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {stats.coach_performance.map(c => (
                      <tr key={c.id} className="table-row-hover">
                        <td className="px-5 py-3.5 font-medium text-slate-800">{c.name}</td>
                        <td className="px-5 py-3.5 text-slate-600">{c.students_count}</td>
                        <td className="px-5 py-3.5 text-slate-600">{c.steps_count}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-100 rounded-full h-1.5 max-w-[80px]">
                              <div className="progress-bar" style={{ width: `${c.progress}%` }} />
                            </div>
                            <span className="text-slate-700 font-medium">{c.progress}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Statistics Tab - Advanced Analytics */}
      {activeTab === "statistics" && (
        <div className="space-y-8 animate-fade-in">
          {/* Filters */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="text-sm font-medium text-slate-700">Filtres</span>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-500">Coach:</label>
                <select 
                  value={filterCoach} 
                  onChange={(e) => setFilterCoach(e.target.value)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                  data-testid="filter-coach"
                >
                  <option value="all">Tous les coachs</option>
                  {coaches.map(c => (
                    <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-500">Ancienneté:</label>
                <select 
                  value={filterDate} 
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                  data-testid="filter-date"
                >
                  {dateFilters.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
              
              {(filterCoach !== "all" || filterDate !== "all") && (
                <button 
                  onClick={() => { setFilterCoach("all"); setFilterDate("all"); }}
                  className="text-sm text-violet-500 hover:text-violet-700 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Réinitialiser
                </button>
              )}
            </div>
          </div>

          {/* Risk Overview Cards - Using filtered stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="glass-card rounded-2xl p-5" style={{ borderColor: "rgba(154, 59, 46, 0.25)" }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="icon-circle icon-circle--rouge">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <p className="metric-number" style={{ color: "var(--rouge)" }}>
                    {filteredStats.high}
                  </p>
                  <p className="metric-label" style={{ color: "var(--rouge-dark)" }}>Risque élevé</p>
                </div>
              </div>
              <p className="text-xs" style={{ color: "var(--rouge)" }}>Élèves nécessitant une attention immédiate</p>
            </div>
            
            <div className="glass-card rounded-2xl p-5" style={{ borderColor: "var(--gold-soft)" }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="icon-circle">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="metric-number" style={{ color: "var(--gold-dark)" }}>
                    {filteredStats.medium}
                  </p>
                  <p className="metric-label" style={{ color: "var(--gold-dark)" }}>Risque modéré</p>
                </div>
              </div>
              <p className="text-xs" style={{ color: "var(--gold-dark)" }}>Élèves à surveiller</p>
            </div>
            
            <div className="glass-card rounded-2xl p-5" style={{ borderColor: "rgba(31, 111, 107, 0.25)" }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="icon-circle icon-circle--teal">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="metric-number" style={{ color: "var(--teal)" }}>
                    {filteredStats.low}
                  </p>
                  <p className="metric-label" style={{ color: "var(--teal-dark)" }}>En bonne voie</p>
                </div>
              </div>
              <p className="text-xs" style={{ color: "var(--teal)" }}>Élèves progressant normalement</p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Distribution Pie Chart */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <h3 className="font-semibold text-slate-800 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                Répartition des risques
              </h3>
              {riskDistributionData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={riskDistributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    >
                      {riskDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-400">
                  Aucune donnée disponible
                </div>
              )}
            </div>

            {/* Integration Date Distribution Bar Chart */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <h3 className="font-semibold text-slate-800 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                Ancienneté des élèves
              </h3>
              {integrationData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={integrationData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
                      formatter={(value) => [`${value} élèves`, "Nombre"]}
                    />
                    <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-400">
                  Aucune donnée disponible
                </div>
              )}
            </div>
          </div>

          {/* High Risk Students Alert */}
          {filteredRiskScores.filter(r => r.level === "high").length > 0 && (
            <div className="rounded-2xl p-5" style={{ background: "var(--rouge-soft)", border: "1px solid rgba(154, 59, 46, 0.3)" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="icon-circle icon-circle--rouge">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold" style={{ fontFamily: "var(--serif)", color: "var(--rouge-dark)" }}>Alertes - Élèves à risque élevé</h3>
                  <p className="text-sm" style={{ color: "var(--rouge)" }}>Ces élèves nécessitent une intervention rapide</p>
                </div>
              </div>
              <div className="space-y-3">
                {filteredRiskScores.filter(r => r.level === "high").map(student => (
                  <div key={student.student_id} className="bg-white rounded-xl p-4 border border-red-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                          <span className="text-red-600 font-bold text-sm">
                            {student.student_name?.split(" ").map(n => n[0]).join("")}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{student.student_name}</p>
                          <p className="text-xs text-slate-500">{student.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg">
                          <span className="font-bold">{student.score}%</span>
                          <span className="text-xs">risque</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Étape: {student.current_stage}</p>
                      </div>
                    </div>
                    {student.factors?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {student.factors.map((f, i) => (
                          <span key={i} className={`text-xs px-2 py-1 rounded-lg ${
                            f.impact === "high" ? "bg-red-100 text-red-700" :
                            f.impact === "medium" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>
                            {f.name}: {f.value}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Students Risk Table - Using filtered data */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <div className="p-5 border-b border-slate-50 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>
                <KPITooltip content={KPI_TOOLTIPS.riskScore} position="right">
                  Tous les élèves - Score de risque
                </KPITooltip>
              </h3>
              <span className="text-sm text-slate-500">{filteredRiskScores.length} élève{filteredRiskScores.length > 1 ? "s" : ""}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Élève</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <KPITooltip content={KPI_TOOLTIPS.progression} position="bottom">Étape actuelle</KPITooltip>
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <KPITooltip content={KPI_TOOLTIPS.riskScore} position="bottom">Score de risque</KPITooltip>
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <KPITooltip content="Facteurs contribuant au score de risque : inactivité, retards, étapes en attente, etc." position="bottom">Facteurs</KPITooltip>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filteredRiskScores.map(student => (
                    <tr key={student.student_id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            student.level === "high" ? "bg-red-100" :
                            student.level === "medium" ? "bg-amber-100" :
                            "bg-emerald-100"
                          }`}>
                            <span className={`text-xs font-bold ${
                              student.level === "high" ? "text-red-600" :
                              student.level === "medium" ? "text-amber-600" :
                              "text-emerald-600"
                            }`}>
                              {student.student_name?.split(" ").map(n => n[0]).join("")}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{student.student_name}</p>
                            <p className="text-xs text-slate-500">{student.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-slate-600">
                        {student.current_stage === "completed" ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Terminé
                          </span>
                        ) : student.current_stage}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                student.level === "high" ? "bg-red-500" :
                                student.level === "medium" ? "bg-amber-500" :
                                "bg-emerald-500"
                              }`}
                              style={{ width: `${student.score}%` }}
                            />
                          </div>
                          <span className={`text-sm font-semibold ${
                            student.level === "high" ? "text-red-600" :
                            student.level === "medium" ? "text-amber-600" :
                            "text-emerald-600"
                          }`}>
                            {student.score}%
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex flex-wrap gap-1">
                          {student.factors?.slice(0, 2).map((f, i) => (
                            <span key={i} className={`text-xs px-2 py-0.5 rounded ${
                              f.impact === "high" ? "bg-red-100 text-red-700" :
                              f.impact === "medium" ? "bg-amber-100 text-amber-700" :
                              "bg-slate-100 text-slate-600"
                            }`}>
                              {f.name}
                            </span>
                          ))}
                          {student.factors?.length === 0 && (
                            <span className="text-xs text-slate-400">Aucun</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Coach Stats Tab - NEW */}
      {activeTab === "coach-stats" && (
        <div className="space-y-8 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <div className="icon-circle icon-circle--ink">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold" style={{ fontFamily: "var(--serif)", color: "var(--ink)" }}>
                Statistiques par Coach
              </h2>
              <p className="text-sm" style={{ color: "var(--muted)" }}>{coachStats.length} coach{coachStats.length > 1 ? "s" : ""} actif{coachStats.length > 1 ? "s" : ""}</p>
            </div>
          </div>

          {/* Coach Comparison Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Students per Coach */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <h3 className="font-semibold text-slate-800 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                <KPITooltip content={KPI_TOOLTIPS.coachStudentsCount} position="right">
                  Nombre d'élèves par Coach
                </KPITooltip>
              </h3>
              {coachPerformanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={coachPerformanceData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={80} />
                    <Tooltip 
                      contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
                      formatter={(value, name, props) => [`${value} élèves`, props.payload.fullName]}
                    />
                    <Bar dataKey="eleves" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-slate-400">
                  Aucun coach actif
                </div>
              )}
            </div>

            {/* Success Rate per Coach */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <h3 className="font-semibold text-slate-800 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                <KPITooltip content={KPI_TOOLTIPS.coachSuccessRate} position="right">
                  Taux de réussite par Coach
                </KPITooltip>
              </h3>
              {coachPerformanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={coachPerformanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                    <Tooltip 
                      contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
                      formatter={(value, name, props) => [`${value}%`, props.payload.fullName]}
                    />
                    <Bar dataKey="reussite" fill="#10b981" radius={[4, 4, 0, 0]} name="Taux de réussite" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-slate-400">
                  Aucun coach actif
                </div>
              )}
            </div>

            {/* Average Risk Score per Coach */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <h3 className="font-semibold text-slate-800 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                <KPITooltip content={KPI_TOOLTIPS.coachAvgRisk} position="right">
                  Score de risque moyen par Coach
                </KPITooltip>
              </h3>
              {coachPerformanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={coachPerformanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                    <Tooltip 
                      contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
                      formatter={(value, name, props) => [`${value}%`, props.payload.fullName]}
                    />
                    <Bar dataKey="risque" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Score de risque moyen" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-slate-400">
                  Aucun coach actif
                </div>
              )}
            </div>

            {/* Risk Distribution per Coach */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <h3 className="font-semibold text-slate-800 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                <KPITooltip content="Nombre d'élèves par niveau de risque pour chaque coach. Permet d'identifier les coachs avec beaucoup d'élèves à risque." position="right">
                  Distribution des risques élèves
                </KPITooltip>
              </h3>
              {coachStats.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={coachStats.map(c => ({
                    name: c.name.split(" ")[0],
                    high: c.high_risk,
                    medium: c.medium_risk,
                    low: c.low_risk
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0" }} />
                    <Legend />
                    <Bar dataKey="high" stackId="a" fill="#ef4444" name="Risque élevé" />
                    <Bar dataKey="medium" stackId="a" fill="#f59e0b" name="Risque modéré" />
                    <Bar dataKey="low" stackId="a" fill="#10b981" name="En bonne voie" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-slate-400">
                  Aucun coach actif
                </div>
              )}
            </div>
          </div>

          {/* Detailed Coach Table */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <div className="p-5 border-b border-slate-50">
              <h3 className="font-semibold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>
                Détail par Coach
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Coach</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <KPITooltip content={KPI_TOOLTIPS.coachStudentsCount} position="bottom">Élèves</KPITooltip>
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <KPITooltip content={KPI_TOOLTIPS.coachAvgRisk} position="bottom">Risque moyen</KPITooltip>
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <KPITooltip content="Répartition des élèves selon leur niveau de risque d'abandon (élevé ≥60%, modéré 30-59%, OK <30%)." position="bottom">Répartition risques</KPITooltip>
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <KPITooltip content={KPI_TOOLTIPS.coachSuccessRate} position="bottom">Taux réussite</KPITooltip>
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <KPITooltip content={KPI_TOOLTIPS.activityMonth} position="bottom">Activité (30j)</KPITooltip>
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Calendly</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {coachStats.map(c => (
                    <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, var(--gold), var(--gold-dark))" }}>
                            <span className="text-white text-xs font-bold">{c.name.split(" ").map(n => n[0]).join("")}</span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{c.name}</p>
                            <p className="text-xs text-slate-500">{c.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-violet-100 text-violet-700 font-bold">
                          {c.students_count}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                c.avg_risk_score >= 60 ? "bg-red-500" :
                                c.avg_risk_score >= 30 ? "bg-amber-500" :
                                "bg-emerald-500"
                              }`}
                              style={{ width: `${c.avg_risk_score}%` }}
                            />
                          </div>
                          <span className={`text-sm font-semibold ${
                            c.avg_risk_score >= 60 ? "text-red-600" :
                            c.avg_risk_score >= 30 ? "text-amber-600" :
                            "text-emerald-600"
                          }`}>
                            {c.avg_risk_score}%
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1">
                          {c.high_risk > 0 && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                              {c.high_risk} élevé
                            </span>
                          )}
                          {c.medium_risk > 0 && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                              {c.medium_risk} modéré
                            </span>
                          )}
                          {c.low_risk > 0 && (
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                              {c.low_risk} OK
                            </span>
                          )}
                          {c.students_count === 0 && (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-medium ${
                          c.success_rate >= 70 ? "bg-emerald-100 text-emerald-700" :
                          c.success_rate >= 40 ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-600"
                        }`}>
                          {c.success_rate}%
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="text-slate-600">{c.recent_activity} sous-étapes</span>
                      </td>
                      <td className="px-5 py-3.5">
                        {c.calendly_configured ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Configuré
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-slate-400">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Non
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === "users" && (
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">{users.length} utilisateur{users.length > 1 ? "s" : ""}</p>
            <button
              data-testid="add-user-btn"
              onClick={() => { setEditingUser(null); setUserForm({ email: "", password: "", first_name: "", last_name: "", role: "student" }); setShowUserModal(true); }}
              className="btn-gold text-sm flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Ajouter
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Nom</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Rôle</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {users.map(u => (
                    <tr key={u.id} className="table-row-hover" data-testid={`user-row-${u.id}`}>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, var(--gold), var(--gold-dark))" }}>
                            <span className="text-white text-xs font-bold">{u.first_name?.[0]}{u.last_name?.[0]}</span>
                          </div>
                          <span className="font-medium text-slate-800">{u.first_name} {u.last_name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-slate-500">{u.email}</td>
                      <td className="px-5 py-3.5"><StatusBadge role={u.role} /></td>
                      <td className="px-5 py-3.5">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${u.is_active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                          {u.is_active ? "Actif" : "Inactif"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          {u.role === "student" && (
                            <button
                              data-testid={`view-dossier-${u.id}`}
                              onClick={() => setViewingStudent(u)}
                              className="text-teal-700 hover:text-teal-900 transition-colors p-1.5 rounded-lg hover:bg-teal-50"
                              title="Voir le dossier complet"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          )}
                          <button data-testid={`edit-user-${u.id}`} onClick={() => { setEditingUser(u); setUserForm({ ...u, password: "" }); setShowUserModal(true); }} className="text-gold-dark hover:text-ink transition-colors p-1.5 rounded-lg hover:bg-gold-bg">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button onClick={() => handleToggleActive(u)} className="text-amber-500 hover:text-amber-700 transition-colors p-1 rounded-lg hover:bg-amber-50">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={u.is_active ? "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"} /></svg>
                          </button>
                          <button data-testid={`delete-user-${u.id}`} onClick={() => handleDeleteUser(u.id)} className="text-red-400 hover:text-red-600 transition-colors p-1 rounded-lg hover:bg-red-50">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Assignments Tab */}
      {activeTab === "assignments" && (
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">{assignments.length} affectation{assignments.length > 1 ? "s" : ""}</p>
            <button
              data-testid="add-assignment-btn"
              onClick={() => { setAssignForm({ student_id: "", coach_id: "" }); setShowAssignModal(true); }}
              className="btn-gold text-sm flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Affecter
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            {assignments.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <svg className="w-12 h-12 mx-auto mb-3 text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                </svg>
                <p>Aucune affectation</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Élève</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Coach</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {assignments.map(a => (
                      <tr key={a.id} className="table-row-hover">
                        <td className="px-5 py-3.5 font-medium text-slate-800">{a.student_name}</td>
                        <td className="px-5 py-3.5 text-slate-600">{a.coach_name}</td>
                        <td className="px-5 py-3.5 text-slate-500 text-xs">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString("fr-FR") : "-"}</td>
                        <td className="px-5 py-3.5">
                          <button onClick={() => handleDeleteAssignment(a.id)} className="text-red-400 hover:text-red-600 transition-colors p-1 rounded-lg hover:bg-red-50">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "relances" && (
        <div className="space-y-6 animate-fade-in" data-testid="admin-relances-tab">
          <ReminderSettingsCard />
          <NotificationPreferences />
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="user-modal">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md animate-fade-in-up">
            <h3 className="text-lg font-semibold text-slate-800 mb-5" style={{ fontFamily: "Outfit, sans-serif" }}>
              {editingUser ? "Modifier l'utilisateur" : "Nouvel utilisateur"}
            </h3>
            <div className="space-y-3">
              {!editingUser && (
                <input data-testid="modal-email" type="email" placeholder="Email" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-500" value={userForm.email} onChange={e => setUserForm({ ...userForm, email: e.target.value })} />
              )}
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="modal-firstname" type="text" placeholder="Prénom" className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-500" value={userForm.first_name} onChange={e => setUserForm({ ...userForm, first_name: e.target.value })} />
                <input data-testid="modal-lastname" type="text" placeholder="Nom" className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-500" value={userForm.last_name} onChange={e => setUserForm({ ...userForm, last_name: e.target.value })} />
              </div>
              <select data-testid="modal-role" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-500" value={userForm.role} onChange={e => setUserForm({ ...userForm, role: e.target.value })}>
                <option value="student">Élève</option>
                <option value="coach">Coach</option>
                <option value="admin">Admin</option>
              </select>
              <input data-testid="modal-password" type="password" placeholder={editingUser ? "Nouveau mot de passe (optionnel)" : "Mot de passe"} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-500" value={userForm.password} onChange={e => setUserForm({ ...userForm, password: e.target.value })} />
              {editingUser && (
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={userForm.is_active !== false} onChange={e => setUserForm({ ...userForm, is_active: e.target.checked })} className="rounded" />
                  Compte actif
                </label>
              )}
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setShowUserModal(false); setEditingUser(null); }} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">Annuler</button>
              <button data-testid="save-user-btn" onClick={handleSaveUser} className="btn-primary flex-1 py-2.5 text-sm">
                {editingUser ? "Modifier" : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md animate-fade-in-up">
            <h3 className="text-lg font-semibold text-slate-800 mb-5" style={{ fontFamily: "Outfit, sans-serif" }}>Nouvelle affectation</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">Élève</label>
                <select data-testid="assign-student" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" value={assignForm.student_id} onChange={e => setAssignForm({ ...assignForm, student_id: e.target.value })}>
                  <option value="">Sélectionner un élève</option>
                  {unassignedStudents.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">Coach</label>
                <select data-testid="assign-coach" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" value={assignForm.coach_id} onChange={e => setAssignForm({ ...assignForm, coach_id: e.target.value })}>
                  <option value="">Sélectionner un coach</option>
                  {coaches.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowAssignModal(false)} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">Annuler</button>
              <button data-testid="confirm-assign-btn" onClick={handleAssign} disabled={!assignForm.student_id || !assignForm.coach_id} className="btn-primary flex-1 py-2.5 text-sm disabled:opacity-50">Affecter</button>
            </div>
          </div>
        </div>
      )}

      {viewingStudent && (
        <AdminStudentDossier
          student={viewingStudent}
          onClose={() => setViewingStudent(null)}
        />
      )}
    </div>
  );
}
